import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Paper,
    CircularProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    Dialog as ConfirmDialog,
    DialogContentText,
    IconButton,
    Tooltip,
    Snackbar,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StopIcon from '@mui/icons-material/Stop';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom'; // Add this import
import scanApi from '../../services/api';

const ScanHistoryView = () => {
    const navigate = useNavigate(); // Add this hook
    const [scanHistory, setScanHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [scanToDelete, setScanToDelete] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);

    const fetchHistory = async () => {
        try {
            const scans = await scanApi.getScanHistory();
            setScanHistory(scans);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch scan history:', err);
            setError('Failed to fetch scan history');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHistory();
        const interval = setInterval(fetchHistory, 10000);

        return () => clearInterval(interval);
    }, []);

    const handleViewDetails = (scan) => {
        const scanId = scan.scan_id || scan.id;
        navigate(`/scan/${scanId}`);
    };

    const handleStopScan = async (scanId) => {
        try {
            await scanApi.stopScan(scanId);
            await fetchHistory(); // Use the defined fetchHistory function
        } catch (error) {
            console.error('Failed to stop scan:', error);
            setError('Failed to stop scan');
        }
    };

    // Aggiungere funzione per gestire la pausa
    const handlePauseScan = async (scanId) => {
        try {
            await scanApi.pauseScan(scanId);
            await fetchHistory(); // Refresh della lista
        } catch (error) {
            console.error('Failed to pause scan:', error);
            setError('Failed to pause scan');
        }
    };

    const handleDeleteClick = (scan) => {
        setScanToDelete(scan);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            setLoading(true);
            const scanId = scanToDelete?.scan_id;
            if (!scanId) {
                throw new Error('Invalid scan ID');
            }

            await scanApi.deleteScan(scanId);
            setDeleteConfirmOpen(false);
            setScanToDelete(null);
            await fetchHistory();
            setError(null);
        } catch (error) {
            console.error('Failed to delete scan:', error);
            setError(error.response?.data?.error || 'Failed to delete scan');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setScanToDelete(null);
    };

    const truncateUrl = (url, maxLength = 50) => {
        if (url.length <= maxLength) return url;
        const start = url.substring(0, Math.floor(maxLength / 2));
        const end = url.substring(url.length - Math.floor(maxLength / 2));
        return `${start}...${end}`;
    };

    const handleCopyUrl = (url) => {
        navigator.clipboard.writeText(url).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        });
    };

    const renderActionButtons = (scan) => (
        <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 36px)', // Fixed 3-column grid
            gap: 1,
            justifyContent: 'center',
            '& .MuiIconButton-root': {
                width: '36px',
                height: '36px',
            }
        }}>
            <Tooltip title="View Details">
                <IconButton
                    onClick={() => handleViewDetails(scan)}
                    size="small"
                    sx={{
                        color: 'primary.main',
                        '&:hover': {
                            backgroundColor: 'primary.light',
                        },
                    }}
                >
                    <VisibilityIcon />
                </IconButton>
            </Tooltip>
            
            {/* Always render the stop button container, but disable if not applicable */}
            <Tooltip title={['processing', 'starting', 'queued'].includes(scan.status) ? "Stop Scan" : ""}>
                <span> {/* Wrapper to handle disabled Tooltip */}
                    <IconButton
                        size="small"
                        onClick={() => handleStopScan(scan.scan_id || scan.id)}
                        disabled={!['processing', 'starting', 'queued'].includes(scan.status)}
                        sx={{
                            color: 'error.main',
                            '&:hover': {
                                backgroundColor: 'error.light',
                            },
                            '&.Mui-disabled': {
                                opacity: 0.3,
                                color: 'error.main',
                            }
                        }}
                    >
                        <StopIcon />
                    </IconButton>
                </span>
            </Tooltip>
            
            <Tooltip title="Delete">
                <IconButton
                    size="small"
                    onClick={() => handleDeleteClick(scan)}
                    sx={{
                        color: 'error.main',
                        '&:hover': {
                            backgroundColor: 'error.light',
                        },
                    }}
                >
                    <DeleteIcon />
                </IconButton>
            </Tooltip>
        </Box>
    );

    const renderStatusChip = (status) => (
        <Chip 
            label={status}
            color={
                status === 'completed' ? 'success' :
                status === 'processing' ? 'warning' :
                status === 'failed' ? 'error' :
                status === 'scheduled' ? 'info' : // Add scheduled status
                'default'
            }
            sx={{
                fontWeight: 500,
                minWidth: '90px',
                '&.MuiChip-colorSuccess': {
                    backgroundColor: 'success.light',
                    color: 'success.dark',
                },
                '&.MuiChip-colorWarning': {
                    backgroundColor: 'warning.light',
                    color: 'warning.dark',
                },
                '&.MuiChip-colorError': {
                    backgroundColor: 'error.light',
                    color: 'error.dark',
                }
            }}
        />
    );

    const formatScheduledTime = (timeString) => {
        if (!timeString) return '';
        
        try {
            // Handle YYYYMMDDTHHmmss format
            const matches = timeString.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})$/);
            if (matches) {
                const [_, year, month, day, hour, minute] = matches;
                return `${month}/${day}/${year} ${hour}:${minute}`;
            }

            // Fallback to standard date parsing
            const date = new Date(timeString);
            if (!isNaN(date.getTime())) {
                return date.toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                });
            }

            console.error('Invalid date format:', timeString);
            return '';
        } catch (error) {
            console.error('Error formatting date:', error, timeString);
            return '';
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">
                    {error}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ 
                    fontWeight: 600,
                    mb: 4
                }}
            >
                Scan History
            </Typography>

            {error && (
                <Alert 
                    severity="error" 
                    sx={{ 
                        mb: 3,
                        borderRadius: 2
                    }} 
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            <TableContainer 
                component={Paper} 
                sx={{ 
                    overflow: 'hidden',
                    '& .MuiTableCell-root': {
                        borderColor: 'divider'
                    }
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell 
                                sx={{ 
                                    maxWidth: '300px',  // Limit URL column width
                                    width: '30%'        // Set relative width
                                }}
                            >
                                Target URL
                            </TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell>Start Time</TableCell>
                            <TableCell align="center">Vulnerabilities</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {scanHistory.map((scan) => (
                            <TableRow key={scan.id || scan.scan_id}>
                                <TableCell 
                                    sx={{
                                        maxWidth: '300px',
                                        position: 'relative',
                                        '&:hover .copy-button': {
                                            opacity: 1,
                                        }
                                    }}
                                >
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 1,
                                        pr: 4 // Space for copy button
                                    }}>
                                        <Tooltip title={scan.target_url} placement="top">
                                            <Typography
                                                sx={{
                                                    fontSize: '0.875rem',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        color: 'primary.main',
                                                    }
                                                }}
                                                onClick={() => handleCopyUrl(scan.target_url)}
                                            >
                                                {truncateUrl(scan.target_url)}
                                            </Typography>
                                        </Tooltip>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopyUrl(scan.target_url)}
                                            className="copy-button"
                                            sx={{
                                                opacity: 0,
                                                transition: 'opacity 0.2s',
                                                position: 'absolute',
                                                right: 8,
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                }
                                            }}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                                <TableCell align="center">
                                    {renderStatusChip(scan.status)}
                                </TableCell>
                                <TableCell>
                                    {scan.status === 'scheduled' && scan.scheduled_time ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography component="span" color="text.secondary">
                                                Scheduled for:
                                            </Typography>
                                            <Typography component="span">
                                                {formatScheduledTime(scan.scheduled_time)}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        new Date(scan.start_time).toLocaleString()
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                        {(() => {
                                            const counts = JSON.parse(scan.severity_counts || '{"high":0,"medium":0,"low":0}');
                                            return (
                                                <>
                                                    <Chip
                                                        label={`${counts.high || 0}`}
                                                        color="error"
                                                        size="small"
                                                        sx={{
                                                            minWidth: '60px',
                                                            fontWeight: 600,
                                                            '&::before': {
                                                                content: '"H"',
                                                                marginRight: '4px',
                                                                fontSize: '0.75rem',
                                                                opacity: 0.7
                                                            }
                                                        }}
                                                    />
                                                    <Chip
                                                        label={`${counts.medium || 0}`}
                                                        color="warning"
                                                        size="small"
                                                        sx={{
                                                            minWidth: '60px',
                                                            fontWeight: 600,
                                                            '&::before': {
                                                                content: '"M"',
                                                                marginRight: '4px',
                                                                fontSize: '0.75rem',
                                                                opacity: 0.7
                                                            }
                                                        }}
                                                    />
                                                    <Chip
                                                        label={`${counts.low || 0}`}
                                                        color="success"
                                                        size="small"
                                                        sx={{
                                                            minWidth: '60px',
                                                            fontWeight: 600,
                                                            '&::before': {
                                                                content: '"L"',
                                                                marginRight: '4px',
                                                                fontSize: '0.75rem',
                                                                opacity: 0.7
                                                            }
                                                        }}
                                                    />
                                                </>
                                            );
                                        })()}
                                    </Box>
                                </TableCell>
                                <TableCell align="center">
                                    {renderActionButtons(scan)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Snackbar
                open={copySuccess}
                autoHideDuration={2000}
                onClose={() => setCopySuccess(false)}
                message="URL copied to clipboard"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />

            <ConfirmDialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete this scan? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </ConfirmDialog>
        </Box>
    );
};

export default ScanHistoryView;
